import { SourceLinks } from '@/components/ui/source-links';
import { ImportanceBadge } from '@/components/ui/importance-badge';
import { DatePopover } from '@/components/ui/date-popover';
import { Badge } from '@/components/ui/badge';

interface NarrativeSettingCardProps {
  narrativeSetting: HistoricalEventsByBook['narrativeSetting'];
}

/**
 * Shared component for displaying narrative setting (when/where a book's story takes place)
 * Used in both the historical context page and the capability modal
 */
export function NarrativeSettingCard({ narrativeSetting }: NarrativeSettingCardProps) {
  if (!narrativeSetting) return null;

  return (
    <div className="border rounded-lg p-4 space-y-3">
      {/* Title and Date */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-semibold">{narrativeSetting.title}</h4>
          <div className="flex items-center gap-2">
            <ImportanceBadge importance={narrativeSetting.importance} />
            <DatePopover
              date={narrativeSetting.narrativeSettingDate}
              alternativeDates={narrativeSetting.narrativeSettingDateAlternatives}
              title="Narrative Setting Date"
            />
          </div>
        </div>
      </div>

      {/* Summary */}
      {narrativeSetting.summary && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">Summary</p>
          <p className="text-sm">{narrativeSetting.summary}</p>
        </div>
      )}

      {/* Region and Empire */}
      <div className="flex flex-wrap gap-2">
        {narrativeSetting.region && (
          <Badge variant="outline" className="text-xs">
            📍 {narrativeSetting.region}
          </Badge>
        )}
        {narrativeSetting.empire && (
          <Badge variant="outline" className="text-xs">
            🏛️ {narrativeSetting.empire} Empire
          </Badge>
        )}
      </div>

      {/* Key Figures */}
      {narrativeSetting.keyFigures && narrativeSetting.keyFigures.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Key Figures</p>
          <div className="flex flex-wrap gap-1">
            {narrativeSetting.keyFigures.map((figure, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {figure}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {narrativeSetting.description && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Historical Setting</p>
          <p className="text-sm leading-relaxed whitespace-pre-line">{narrativeSetting.description}</p>
        </div>
      )}

      {/* Relevance */}
      {narrativeSetting.relevance && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-1">Why This Matters</p>
          <p className="text-sm leading-relaxed whitespace-pre-line">{narrativeSetting.relevance}</p>
        </div>
      )}

      {/* Sources */}
      {narrativeSetting.sources && narrativeSetting.sources.length > 0 && (
        <div className="pt-2 border-t">
          <SourceLinks sources={narrativeSetting.sources} />
        </div>
      )}
    </div>
  );
}

